<template>
  <div class="activity-feed">
    <div class="d-flex align-center justify-space-between mb-3">
      <h2 class="text-h6 font-weight-bold">{{ $t("t_latest_activity") }}</h2>
    </div>

    <v-card class="rounded-lg elevation-2">
      <!-- Loading State -->
      <div v-if="loading" class="pa-4 text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <p class="mt-2 text-caption grey--text">{{ $t("t_loading_activity") }}</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="allItems.length === 0" class="pa-8 text-center">
        <v-icon size="64" color="grey lighten-1">mdi-calendar-check-outline</v-icon>
        <p class="mt-3 text-subtitle-1 grey--text">{{ $t("t_no_recent_activity") }}</p>
        <p class="text-caption grey--text">{{ $t("t_recent_activity_description") }}</p>
      </div>

      <!-- Activity List Grouped by Date -->
      <div v-else>
        <div v-for="(group, groupIndex) in groupedByDate" :key="`group-${groupIndex}`" class="date-group">
          <!-- Date Header -->
          <div class="date-header px-4 py-2">
            <span class="text-caption font-weight-bold grey--text text--darken-1">{{ group.date }}</span>
          </div>

          <!-- Items for this date -->
          <v-list class="py-0">
            <div v-for="(item, index) in group.items" :key="`activity-${item.type}-${item.id}`">
              <v-list-item
                @click="navigateToItem(item)"
                class="activity-item"
                :class="`activity-item--${item.type}`"
                :style="{ borderLeft: `4px solid ${getSectionColor(item.type)}` }"
              >
                <!-- Icon without background -->
                <v-list-item-avatar tile color="transparent" class="activity-icon">
                  <v-icon :color="getSectionColor(item.type)" size="28">
                    {{ getSectionIcon(item) }}
                  </v-icon>
                </v-list-item-avatar>

                <!-- Content -->
                <v-list-item-content class="activity-content">
                  <div v-if="item.status" class="activity-status-badge">
                    <div
                      class="status-badge-block"
                      v-if="item.urgent && item.type === 'forms'"
                      :class="`status-badge--error mr-1`"
                    >
                      <v-icon
                        x-small
                        color="yellow darken-2"
                        size="18"
                        class="mr-1"
                        title="Urgent"
                        style="top: -1px; position: relative"
                      >
                        mdi-alert-circle-outline
                      </v-icon>
                      Urgent
                    </div>
                    <div class="status-badge-block" :class="`status-badge--${getStatusColor(item.status)}`">
                      {{ getStatusDisplayName(item.status) }}
                    </div>
                  </div>

                  <div class="text-overline activity-type-label" :style="{ color: getSectionColor(item.type) }">
                    {{ item.typeLabel }}
                  </div>

                  <v-list-item-title class="font-weight-medium mb-1 d-flex align-center activity-title">
                    <span class="flex-grow-1">{{ item.title }}</span>
                  </v-list-item-title>

                  <!-- Subtitle/Description -->
                  <v-list-item-subtitle v-if="item.type === 'issues' && item.subtitle" class="text-wrap">
                    <tag-chips :tags="item.subtitle" :displayCount="6" />
                  </v-list-item-subtitle>

                  <v-list-item-subtitle v-else-if="item.subtitle" class="text-wrap">
                    {{ item.subtitle }}
                  </v-list-item-subtitle>

                  <v-list-item-subtitle
                    v-if="item.dueDate && !isAssignmentCompleted(item)"
                    class="text-caption"
                    :class="[
                      isDueDatePast(item.dueDate) ? 'orange--text' : 'grey--text text--lighten-1',
                      item.type === 'forms' ? 'mt-1' : '',
                    ]"
                  >
                    {{ $t("t_due") }} {{ formatDueDate(item.dueDate) }}
                  </v-list-item-subtitle>

                  <v-list-item-subtitle
                    v-if="(item.type === 'forms' || item.type === 'planners') && item.submissionInfo"
                    class="text-caption mt-1"
                    :class="getSubmissionInfoClass(item.submissionInfo)"
                  >
                    {{ item.submissionInfo }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>

              <v-divider v-if="index < group.items.length - 1"></v-divider>
            </div>
          </v-list>
        </div>
      </div>

      <div v-if="loadingMore" class="d-flex justify-center my-2">
        <v-progress-circular indeterminate color="primary" size="32" />
      </div>

      <div v-show="hasMore" ref="infiniteScrollTrigger" style="height: 1px; margin-top: 20px"></div>
    </v-card>
  </div>
</template>

<script>
import { FormService, PlannerService, IssueService, MessageService } from "@/services";
import { DateTime } from "luxon";
import { mapGetters } from "vuex";
import { TagChips } from "@ranes-inc/shared-lib";
import statusBadgeMixin from "@/mixins/statusBadge";

export default {
  name: "ActivityFeed",
  components: { TagChips },
  mixins: [statusBadgeMixin],

  props: {
    maxItems: {
      type: Number,
      default: 10,
    },
    itemsPerCategory: {
      type: Number,
      default: 10,
    },
  },
  computed: {
    ...mapGetters("settings", ["settings"]),
    issueStatusMap() {
      return {
        o: "open",
        a: "assigned",
        p: "in progress",
        h: "on hold",
        r: "resolved",
        c: "closed",
      };
    },
    groupedByDate() {
      const groups = [];
      let currentDate = null;
      let currentGroup = null;

      this.allItems.forEach((item) => {
        const itemDate = this.getDateLabel(item.date);

        if (itemDate !== currentDate) {
          if (currentGroup) {
            groups.push(currentGroup);
          }
          currentDate = itemDate;
          currentGroup = {
            date: itemDate,
            items: [item],
          };
        } else {
          currentGroup.items.push(item);
        }
      });

      if (currentGroup) {
        groups.push(currentGroup);
      }

      return groups;
    },
    currentLocale() {
      return this.$i18n.locale || this.$store.state?.settings?.lang;
    },
  },
  watch: {
    currentLocale() {
      // Re-process all items when language changes
      this.reprocessItemsForLocale();
    },
  },
  data: () => ({
    loading: false,
    loadingMore: false,
    allItems: [],
    hasMore: true,
    skipCounts: {
      forms: 0,
      planners: 0,
      issues: 0,
      messages: 0,
    },
    observer: null,
    savedScrollPosition: 0,
    navigatedFromFeed: false,
  }),
  methods: {
    async loadActivityFeed(refresh = false) {
      // Prevent multiple simultaneous loads
      if (this.loadingMore || this.loading) return;

      // Reset state if refreshing
      if (refresh) {
        this.allItems = [];
        this.hasMore = true;
        this.skipCounts = {
          forms: 0,
          planners: 0,
          issues: 0,
          messages: 0,
        };
      }

      // Set loading state early to prevent race conditions
      if (
        this.skipCounts.forms === 0 &&
        this.skipCounts.planners === 0 &&
        this.skipCounts.issues === 0 &&
        this.skipCounts.messages === 0
      ) {
        this.loading = true;
      } else {
        this.loadingMore = true;
      }

      try {
        // Fetch all data in parallel using an object to track each type
        const promiseMap = {
          forms: this.fetchFormAssignments(),
          issues: this.fetchIssues(),
          messages: this.fetchMessages(),
        };

        promiseMap.planners = this.fetchPlannerAssignments();

        // Convert to array for Promise.allSettled while keeping track of keys
        const promiseEntries = Object.entries(promiseMap);
        const promises = promiseEntries.map(([, promise]) => promise);
        const responses = await Promise.allSettled(promises);

        const items = [];
        let allEndOfResults = true;

        // Process each response based on its key
        promiseEntries.forEach(([key], index) => {
          const response = responses[index];

          if (response && response.status === "fulfilled") {
            const result = response.value;

            let processedItems = [];
            switch (key) {
              case "forms":
                processedItems = this.processFormAssignments(result.data || result);
                break;
              case "planners":
                processedItems = this.processPlannerAssignments(result.data || result);
                break;
              case "issues":
                processedItems = this.processIssues(result.data || result);
                break;
              case "messages":
                processedItems = this.processMessages(result.data || result);
                break;
            }

            items.push(...processedItems);

            // Update skip count for this category
            this.skipCounts[key] += processedItems.length;

            // Check if this category has more data
            if (result.meta && !result.meta.endOfResults) {
              allEndOfResults = false;
            } else if (processedItems.length === this.itemsPerCategory) {
              // If we got a full batch, assume there might be more
              allEndOfResults = false;
            }
          } else if (response) {
            console.error(`${key} fetch failed:`, response.reason);
          }
        });

        // Add new items to existing items
        items.forEach((item) => {
          // Prevent duplicates
          if (!this.allItems.some((existing) => existing.type === item.type && existing.id === item.id)) {
            this.allItems.push(item);
          }
        });

        // Sort by date (most recent first)
        this.allItems.sort((a, b) => {
          const dateA = a.date ? DateTime.fromISO(a.date) : DateTime.fromMillis(0);
          const dateB = b.date ? DateTime.fromISO(b.date) : DateTime.fromMillis(0);
          if (!dateA.isValid) return 1; // Invalid dates go to end
          if (!dateB.isValid) return -1;
          return dateB.toMillis() - dateA.toMillis();
        });

        // Update hasMore flag
        this.hasMore = !allEndOfResults;
      } catch (error) {
        console.error("Error loading activity feed:", error);
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },

    async fetchFormAssignments() {
      const params = {
        "paging.orderbyfield": "AvailableOn",
        "paging.ascending": false,
        "paging.skipnum": this.skipCounts.forms,
        "paging.takenum": this.itemsPerCategory,
        IncludeSubmissionStatus: true,
      };

      try {
        const response = await FormService.getFormAssignments(params);

        const assignments = response.data?.data || response.data || [];
        assignments.forEach((assignment) => {
          if (assignment.submission) {
            assignment.submittedOn = assignment.submission.submittedOn;
            assignment.startedOn = assignment.submission.startedOn;
            assignment.reviewedOn = assignment.submission.reviewedOn;
            assignment.reviewPass = assignment.submission.reviewPass;
            assignment.reviewedByFriendlyName = assignment.submission.reviewedByFriendlyName;
          }
        });

        return {
          data: assignments,
          meta: response.meta || response.data?.meta,
        };
      } catch (error) {
        console.error("Error fetching form assignments:", error);
      }
    },

    async fetchPlannerAssignments() {
      const now = DateTime.now().toISO();
      const params = {
        "paging.orderbyfield": "UpdatedOn",
        "paging.ascending": false,
        "paging.skipnum": this.skipCounts.planners,
        "paging.takenum": 20,
        DueDateFrom: now, // Only include planners with upcoming due dates (excludes open-ended)
        MaxDueDates: 10, // Limit the number of due dates returned per planner
        IncludeNextDueDate: true,
      };
      const response = await PlannerService.getPlannerAssignments(params);
      return {
        data: response.data?.data || response.data || [],
        meta: response.meta || response.data?.meta,
      };
    },

    async fetchIssues() {
      const params = {
        "paging.orderbyfield": "UpdatedOn",
        "paging.ascending": false,
        "paging.skipnum": this.skipCounts.issues,
        "paging.takenum": this.itemsPerCategory,
      };
      const response = await IssueService.getIssues(params);
      return {
        data: response.data?.data || response.data || [],
        meta: response.meta || response.data?.meta,
      };
    },

    async fetchMessages() {
      const params = {
        "paging.orderbyfield": "SentOn",
        "paging.ascending": false,
        "paging.skipnum": this.skipCounts.messages,
        "paging.takenum": this.itemsPerCategory,
      };
      const response = await MessageService.getMessages(params);
      return {
        data: response.data?.data || response.data || [],
        meta: response.meta || response.data?.meta,
      };
    },

    processFormAssignments(assignments) {
      return assignments.map((assignment) => ({
        id: assignment.id,
        type: "forms",
        typeLabel: this.$t("t_form_assignment"),
        title: assignment.formName || assignment.name || this.$t("t_form"),
        subtitle: assignment.description || null,
        date: assignment.availableOn || assignment.createdOn || assignment.assignedOn,
        dueDate: assignment.expiresOn || null,
        status: this.getFormStatus(assignment),
        submissionInfo: this.getFormSubmissionInfo(assignment),
        urgent: assignment.urgent || false,
        rawData: assignment,
      }));
    },

    processPlannerAssignments(planners) {
      return planners.map((planner) => {
        return {
          id: planner.id,
          type: "planners",
          typeLabel: this.$t("t_planner_assignment"),
          title: planner.plannerName || planner.name || this.$t("t_planner"),
          subtitle: planner.description || planner.plannerDescription || null,
          date: planner.createdOn || planner.startDate || planner.updatedOn,
          dueDate: planner.nextDueDate?.dueDateTime || null,
          status: this.getPlannerStatus(planner.nextDueDate),
          submissionInfo: this.getPlannerSubmissionInfo(planner.nextDueDate),
          rawData: planner,
          selectedDueDate: planner.nextDueDate,
        };
      });
    },
    processIssues(issues) {
      if (!Array.isArray(issues)) {
        console.error("Issues is not an array:", issues);
        return [];
      }

      return issues.map((issue) => {
        // Normalize legacy "n" status to "o" (Open)
        const normalizedIssue = { ...issue };
        if (normalizedIssue.status === "n") {
          normalizedIssue.status = "o";
        }

        return {
          id: normalizedIssue.id,
          type: "issues",
          typeLabel: this.$t("t_log_it"),
          title: normalizedIssue.description || normalizedIssue.title || this.$t("t_issue_report"),
          subtitle: normalizedIssue.tags || null,
          date: normalizedIssue.updatedOn || normalizedIssue.createdOn,
          status: this.getIssueStatus(normalizedIssue),
          rawData: normalizedIssue,
        };
      });
    },

    processMessages(messages) {
      return messages.map((message) => ({
        id: message.id,
        type: "messages",
        typeLabel: this.$t("t_message"),
        title: message.subject || this.$t("t_new_message"),
        subtitle: message.fromUserFriendlyName || message.from || message.senderName || null,
        date: message.sentOn || message.createdOn,
        status: message.readOn ? null : "new",
        rawData: message,
      }));
    },

    reprocessItemsForLocale() {
      // Re-process all items to update translated strings when language changes
      if (!this.allItems || this.allItems.length === 0) return;

      this.allItems = this.allItems.map((item) => {
        const rawData = item.rawData;
        if (!rawData) return item;

        switch (item.type) {
          case "forms": {
            const processed = this.processFormAssignments([rawData]);
            return processed.length > 0 ? processed[0] : item;
          }
          case "planners": {
            const processed = this.processPlannerAssignments([rawData]);
            return processed.length > 0 ? processed[0] : item;
          }
          case "issues": {
            const processed = this.processIssues([rawData]);
            return processed.length > 0 ? processed[0] : item;
          }
          case "messages": {
            const processed = this.processMessages([rawData]);
            return processed.length > 0 ? processed[0] : item;
          }
          default:
            return item;
        }
      });
    },

    getFormStatus(assignment) {
      // Check if the assignment has submission data
      if (assignment.submission) {
        const submission = assignment.submission;

        // Check for reviewed status
        if (submission.reviewedOn) {
          if (submission.reviewPass === true) return "approved";
          if (submission.reviewPass === false) return "rejected";
          return "reviewed";
        }

        if (submission.submittedOn) return "in review";

        // If started but not submitted, check if overdue
        if (submission.startedOn) {
          if (assignment.expiresOn) {
            try {
              const due = DateTime.fromISO(assignment.expiresOn);
              if (due.isValid && due < DateTime.now()) return "overdue";
            } catch (error) {
              console.warn("Invalid due date for assignment:", assignment.expiresOn, error);
            }
          }
          return "in progress";
        }
      }

      // Fallback: check for legacy properties directly on assignment
      if (assignment.reviewedOn) {
        if (assignment.reviewPass === true) return "approved";
        if (assignment.reviewPass === false) return "rejected";
        return "reviewed";
      }
      if (assignment.submittedOn) return "in review";

      // Check if overdue based on expiration date
      if (assignment.expiresOn) {
        try {
          const due = DateTime.fromISO(assignment.expiresOn);
          if (due.isValid && due < DateTime.now()) return "overdue";
        } catch (error) {
          console.warn("Invalid due date for assignment:", assignment.expiresOn, error);
        }
      }

      return "pending";
    },

    getFormSubmissionInfo(assignment) {
      const submission = assignment.submission || assignment;

      // If reviewed, show review info
      if (submission.reviewedOn) {
        const reviewDate = this.formatShortDate(submission.reviewedOn);
        const reviewer = submission.reviewedByFriendlyName || "Reviewer";
        const status =
          submission.reviewPass === true
            ? this.$t("t_approved")
            : submission.reviewPass === false
            ? this.$t("t_rejected")
            : this.$t("t_reviewed");
        return `${status} ${reviewDate} by ${reviewer}`;
      }

      // If submitted but not reviewed, show submission info
      if (submission.submittedOn) {
        const submitDate = this.formatShortDate(submission.submittedOn);
        return `${this.$t("t_submitted")} ${submitDate}`;
      }

      return null;
    },

    getPlannerStatus(dueDate) {
      if (!dueDate) return "pending";

      const submissionStatus = dueDate.submissionStatus;

      // Map submissionStatus: 0=Draft, 1=InProgress, 2=Completed, 3=Cancelled, 4=Deleted
      if (submissionStatus === 2) return "completed";
      if (submissionStatus === 1) return "in progress";
      if (submissionStatus === 3) return "cancelled";
      if (submissionStatus === 4) return "deleted";

      // For Draft (0) or undefined, check if overdue
      if (dueDate.dueDateTime) {
        const due = DateTime.fromISO(dueDate.dueDateTime);
        if (due.isValid && due < DateTime.now()) return "overdue";
      }

      return "pending";
    },

    getPlannerSubmissionInfo(dueDate) {
      if (!dueDate) return null;

      // If completed, show completion date
      if (dueDate.submissionStatus === 2 && dueDate.completedOn) {
        const completedDate = this.formatShortDate(dueDate.completedOn);
        return `${this.$t("t_completed")} ${completedDate}`;
      }

      return null;
    },

    getIssueStatus(issue) {
      // Normalize legacy "n" status to "o" (Open)
      let statusLetter = issue.status;
      if (statusLetter === "n") {
        statusLetter = "o";
      }
      return this.issueStatusMap[statusLetter] || statusLetter;
    },

    getSectionIcon(item) {
      const type = typeof item === "string" ? item : item.type;

      if (type === "forms" && item.rawData) {
        const tags = item.rawData.tags || "";
        if (tags.indexOf("form") !== -1) {
          return "mdi-clipboard";
        } else if (tags.indexOf("audit") !== -1) {
          return "mdi-clipboard-text-search";
        } else if (tags.indexOf("checklist") !== -1) {
          return "mdi-clipboard-text";
        }
        return "mdi-text-box";
      }

      // For other types, use the standard icons
      const icons = {
        forms: "mdi-form-select",
        planners: "mdi-calendar-check",
        issues: "mdi-camera-outline",
        messages: "mdi-cube-send",
      };
      return icons[type] || "mdi-circle";
    },

    getSectionColor(type) {
      const colors = {
        forms: "#42A5F5",
        planners: "#5C6BC0",
        issues: "#EF5350",
        messages: "#66BB6A",
      };
      return colors[type] || "#757575";
    },

    getSubmissionInfoClass(submissionInfo) {
      if (!submissionInfo) return "";

      if (submissionInfo.toLowerCase().includes("approved")) {
        return "success--text";
      } else if (submissionInfo.toLowerCase().includes("rejected")) {
        return "error--text";
      } else if (submissionInfo.toLowerCase().includes("review")) {
        return "info--text";
      }

      return "grey--text";
    },

    getDateLabel(dateString) {
      if (!dateString) {
        console.warn("getDateLabel called with empty dateString");
        return this.$t("t_unknown_date");
      }

      const date = DateTime.fromISO(dateString);

      if (!date.isValid) {
        console.warn("Invalid date string:", dateString, "Parse error:", date.invalidReason);
        return this.$t("t_unknown_date");
      }

      const now = DateTime.now();
      const startOfToday = now.startOf("day");
      const startOfYesterday = startOfToday.minus({ days: 1 });
      const dateStart = date.startOf("day");

      const localized = date.setLocale(this.currentLocale);

      if (dateStart.equals(startOfToday)) {
        return this.$t("t_today");
      } else if (dateStart.equals(startOfYesterday)) {
        return this.$t("t_yesterday");
      } else if (now.diff(date, "days").days < 7) {
        return localized.toFormat("EEEE"); // Day name (e.g., "Monday")
      } else if (date.year === now.year) {
        return localized.toFormat("MMMM d"); // e.g., "October 2"
      } else {
        return localized.toFormat("MMMM d, yyyy"); // e.g., "October 2, 2024"
      }
    },

    formatTime(dateString) {
      if (!dateString) return "";
      const date = DateTime.fromISO(dateString);
      const now = DateTime.now();
      const diff = now.diff(date, ["days", "hours", "minutes"]);

      if (diff.days > 7) {
        return date.toFormat("MMM d");
      } else if (diff.days >= 1) {
        return `${Math.floor(diff.days)}d ago`;
      } else if (diff.hours >= 1) {
        return `${Math.floor(diff.hours)}h ago`;
      } else if (diff.minutes >= 1) {
        return `${Math.floor(diff.minutes)}m ago`;
      } else {
        return "Just now";
      }
    },

    formatShortDate(dateString) {
      if (!dateString) return "";
      const date = DateTime.fromISO(dateString);
      if (!date.isValid) return "";

      const now = DateTime.now();
      const startOfToday = now.startOf("day");
      const dateStart = date.startOf("day");

      const localized = date.setLocale(this.currentLocale);

      if (dateStart.equals(startOfToday)) {
        return this.$t("t_today");
      } else if (date.year === now.year) {
        return localized.toFormat("MMM d"); // e.g., "Nov 12"
      } else {
        return localized.toFormat("MMM d, yyyy"); // e.g., "Nov 12, 2024"
      }
    },

    formatDueDate(dateString) {
      if (!dateString) return "";
      const date = DateTime.fromISO(dateString);
      if (!date.isValid) return "";

      const now = DateTime.now();
      const startOfToday = now.startOf("day");
      const dateStart = date.startOf("day");
      const diff = dateStart.diff(startOfToday, "days").days;
      const localized = date.setLocale(this.currentLocale);

      // If overdue
      if (diff < 0) {
        const absDiff = Math.abs(Math.floor(diff));
        if (absDiff === 1) return this.$t("t_yesterday");
        if (absDiff < 7) return `${absDiff} ${this.$t("t_days_ago")}`;
        return localized.toFormat("MMM d");
      }

      // If today
      if (diff < 1) {
        return this.$t("t_today");
      }

      // If tomorrow
      if (diff < 2) {
        return this.$t("t_tomorrow");
      }

      // If within a week
      if (diff < 7) {
        return localized.toFormat("EEEE"); // Day name
      }

      // If same year
      if (date.year === now.year) {
        return localized.toFormat("MMM d");
      }

      // Otherwise include year
      return localized.toFormat("MMM d, yyyy");
    },

    isDueDatePast(dateString) {
      if (!dateString) return false;
      const date = DateTime.fromISO(dateString);
      if (!date.isValid) return false;
      const now = DateTime.now();
      return date < now;
    },

    isAssignmentCompleted(item) {
      // Hide due date for forms and planners that are submitted or completed
      const status = item.status;

      // For forms: hide if submitted, completed, approved, rejected, or reviewed
      if (item.type === "forms") {
        return (
          status === "completed" ||
          status === "approved" ||
          status === "rejected" ||
          status === "reviewed" ||
          status === "in review" ||
          status === "submitted"
        );
      }

      // For planners: hide if completed
      if (item.type === "planners") {
        return status === "completed";
      }

      return false;
    },

    async navigateToItem(item) {
      const rawData = item.rawData;

      // Mark that we're navigating from activity feed
      this.navigatedFromFeed = true;

      switch (item.type) {
        case "forms":
          // Navigate like FormAssignments: check if has submission started
          this.$router.push({
            name: rawData.submissionId ? "submission" : "assignment",
            params: {
              id: rawData.submissionId ? rawData.submissionId : rawData.id,
            },
          });
          break;

        case "planners": {
          let plannerPath = `/planner/${rawData.plannerId}/${rawData.id}`;
          if (rawData.currentSubmission && !rawData.selectedDueDate) {
            plannerPath += `/0/${rawData.currentSubmission.id}`;
          }
          this.$router.push(plannerPath);
          break;
        }

        case "issues":
          // Navigate to issue detail page
          this.$router.push({ name: "issue", params: { id: rawData.id } });
          break;

        case "messages":
          // Navigate to messages page with message ID to auto-open
          this.$router.push({
            name: "messages",
            query: { messageId: rawData.id },
          });
          break;
      }
    },

    refresh() {
      this.loadActivityFeed(true);
    },

    saveScrollPosition() {
      // Save the current scroll position
      this.savedScrollPosition = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
    },

    restoreScrollPosition() {
      this.$nextTick(() => {
        if (this.savedScrollPosition > 0) {
          window.scrollTo(0, this.savedScrollPosition);
        }
      });
    },

    initInfiniteScroll() {
      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting && this.hasMore && !this.loadingMore && !this.loading) {
            this.loadActivityFeed();
          }
        },
        { threshold: 1.0 },
      );

      if (this.$refs.infiniteScrollTrigger) {
        this.observer.observe(this.$refs.infiniteScrollTrigger);
      }
    },
  },
  mounted() {
    if (this.$online && this.allItems.length === 0) {
      this.loadActivityFeed();
    }
    this.$nextTick(() => {
      this.initInfiniteScroll();
    });
  },
  activated() {
    // Called when component is reactivated from keep-alive

    // Only restore scroll position if user navigated from the activity feed
    if (this.navigatedFromFeed) {
      this.restoreScrollPosition();
      this.navigatedFromFeed = false; // Reset flag
    }

    // Re-initialize infinite scroll observer
    this.$nextTick(() => {
      if (!this.observer && this.$refs.infiniteScrollTrigger) {
        this.initInfiniteScroll();
      }
    });
  },
  deactivated() {
    // Called when component is deactivated (cached by keep-alive)
    // Save scroll position before deactivating
    this.saveScrollPosition();
    // Clean up observer to prevent memory leaks
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/variables";

.activity-feed {
  .date-group {
    &:not(:first-child) {
      margin-top: 16px;
    }
  }
  .v-sheet {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    &:hover {
      background: transparent !important;
    }
  }
  .date-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    span {
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  .activity-item {
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 4px 0 4px 12px;
    position: relative;
    border-radius: 4px;
    margin-bottom: 8px;
    background: #ffffff0a;
    &:hover {
      background-color: rgba(0, 0, 0, 0.02);
    }

    .activity-icon {
      margin-right: 12px;
      min-width: 30px !important;
      width: 30px !important;
      height: 40px;
      flex-shrink: 0;
    }

    .activity-content {
      position: relative;
      flex: 1;
      min-width: 0;
      padding-right: 0;
    }

    .activity-status-badge {
      position: absolute;
      top: 2px;
      right: 15px;
      z-index: 1;
      pointer-events: none;
    }

    .activity-type-label {
      font-weight: 700;
      font-size: 0.65rem;
      letter-spacing: 1px;
      line-height: 1;
      margin-bottom: 4px;
      opacity: 0.9;
      padding-right: 60px; // Reserve space for status badge
    }

    .activity-title {
      padding-right: 60px; // Reserve space for status badge
    }

    .activity-description {
      padding-right: 60px; // Reserve space for status badge
    }
  }
}

.theme--dark {
  .activity-feed {
    .date-header {
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    }

    .activity-item {
      &:hover {
        background-color: rgba(255, 255, 255, 0.05);
      }
    }
  }
}
</style>
